// ── What a planned meal is actually worth ────────────────────────────
// Recipe macros are authored as one per-serving lump, and for most meals that
// is the whole story: a meal is `recipe.macros × servings`.
//
// It stops being the whole story when you buy meat. Meat comes in 2 lb packs,
// not the 1.5 lb the recipe asked for, and you cook the pack. The dish is now
// meatier than the number attached to it — and that number is what reaches
// your food diary.
//
// So for ingredients carrying `macrosPer`, we split the authored figure:
//
//     remainder = authored × servings − Σ (planned amount × per-unit macros)
//     meal      = remainder / servings × portions + Σ (actual amount × per-unit)
//
// The remainder is everything that ISN'T the resolved ingredients — the rice,
// the oil, the aromatics. It's derived, never stored, so editing a recipe's
// macros re-splits it rather than leaving a stale basis behind.
//
// Note this is the same algebra as adding a delta to the authored figure:
//     authored + (actual − planned) × M  ≡  (authored − planned × M) + actual × M
// The remainder form is worth the extra step because it puts the contradiction
// where you can see it. Which brings us to the guard.
//
// ── The guard ───────────────────────────────────────────────────────
// The remainder is the rest of the dish, so it cannot be negative. When it is,
// the two sources disagree: the meat alone claims more protein than the whole
// recipe supposedly has. Recipe macros are currently LLM estimates (see
// chat/prompts.py — "estimate honestly from common nutrition data"), and real
// per-unit figures will contradict them often, not rarely.
//
// A meal in that state reports a `problem` and is refused by the Health push.
// Guessing which source to believe would put a number in a permanent health
// record that nobody can defend; saying "these don't reconcile" is honest and
// fixable.

import { convertAmount, normalizeName } from "./shoppingList";
import { parseQty } from "./format";
import type { Ingredient, Macros, PlannedMeal, Recipe } from "./types";

const MACRO_KEYS: Array<keyof Macros> = [
  "calories",
  "protein",
  "carbs",
  "fat",
  "fiber",
];

export const ZERO_MACROS: Required<Macros> = {
  calories: 0,
  protein: 0,
  carbs: 0,
  fat: 0,
  fiber: 0,
};

/** An ingredient the cook can correct the amount of on a per-meal basis:
 *  it carries per-unit macros, so changing it genuinely changes the meal. */
export type AdjustableIngredient = {
  /** normalizeName(ingredient.name) — the ingredientAmounts key. */
  key: string;
  name: string;
  unit: string;
  /** What the recipe asks for, scaled to THIS meal's portions. The default the
   *  editor opens on, and the baseline the override replaces. */
  plannedAmount: number;
  /** What the cook said they actually used, when they've said. */
  actualAmount: number | null;
};

export type MealMacros = {
  /** What this meal contributes. Equals authored × servings when nothing is
   *  overridden, so untouched plans are byte-for-byte unchanged. */
  macros: Required<Macros>;
  /** An override moved the number away from the authored figure. */
  adjusted: boolean;
  /** Set when the split doesn't reconcile — the rest of the dish came out
   *  negative. Human-readable; shown in On Deck and used to refuse the Health
   *  push. When set, `macros` falls back to the unadjusted authored figure so
   *  the board still shows something rather than a nonsense total. */
  problem?: string;
};

function baseAmount(ing: Ingredient): number | null {
  if (ing.base == null || ing.base === "") return null;
  return typeof ing.base === "number" ? ing.base : parseQty(String(ing.base));
}

/**
 * The ingredients of `recipe` that can be corrected for this meal, with the
 * amounts already scaled to the meal's portions. Empty for every recipe that
 * has no per-unit macros on it, which is the normal case.
 */
export function adjustableIngredients(
  meal: PlannedMeal,
  recipe: Recipe | undefined
): AdjustableIngredient[] {
  if (!recipe) return [];
  const ratio = (meal.servings || 1) / (recipe.servings || 1);
  const out: AdjustableIngredient[] = [];
  for (const group of recipe.ingredientGroups ?? []) {
    for (const ing of group.ingredients ?? []) {
      if (!ing.macrosPer) continue;
      const base = baseAmount(ing);
      if (base == null) continue;
      const key = normalizeName(ing.name ?? "");
      if (!key) continue;
      out.push({
        key,
        name: ing.name,
        unit: ing.unit ?? "",
        plannedAmount: base * ratio,
        actualAmount: meal.ingredientAmounts?.[key] ?? null,
      });
    }
  }
  return out;
}

/**
 * What a planned meal is worth, honouring any per-meal amount corrections.
 *
 * `recipe` is resolved live from the cookbook by the caller, same as every
 * other macro path — the plan stores no nutrition of its own.
 */
export function mealMacros(
  meal: PlannedMeal,
  recipe: Recipe | undefined
): MealMacros {
  const authored = recipe?.macros;
  const portions = meal.servings || 1;
  const plain: Required<Macros> = { ...ZERO_MACROS };
  for (const k of MACRO_KEYS) plain[k] = (authored?.[k] ?? 0) * portions;

  if (!authored || !recipe) return { macros: plain, adjusted: false };

  const yield_ = recipe.servings || 1;
  // Whole-recipe authored total, which is the basis the ingredient amounts are
  // written against (ingredients describe the full yield, macros describe one
  // serving of it).
  const authoredTotal: Required<Macros> = { ...ZERO_MACROS };
  for (const k of MACRO_KEYS) authoredTotal[k] = (authored[k] ?? 0) * yield_;

  // Subtract every resolved ingredient at the amount the RECIPE asks for, and
  // add it back at the amount actually used for this meal. Only ingredients the
  // cook has actually corrected take part — an unresolved one would subtract
  // and re-add the same value, but it would also drag its figure into the
  // guard, and a recipe shouldn't start failing because of an ingredient
  // nobody is adjusting.
  const contributedTotal: Required<Macros> = { ...ZERO_MACROS };
  const actualForMeal: Required<Macros> = { ...ZERO_MACROS };
  let adjusted = false;
  let unconvertible: string | null = null;

  for (const group of recipe.ingredientGroups ?? []) {
    for (const ing of group.ingredients ?? []) {
      const per = ing.macrosPer;
      if (!per) continue;
      const key = normalizeName(ing.name ?? "");
      const override = meal.ingredientAmounts?.[key];
      if (override == null) continue;

      const base = baseAmount(ing);
      if (base == null) continue;

      // Recipe may say ounces while the macros are quoted per pound.
      const plannedInPerUnit = convertAmount(base, ing.unit, per.unit);
      const actualInPerUnit = convertAmount(override, ing.unit, per.unit);
      if (plannedInPerUnit == null || actualInPerUnit == null) {
        unconvertible = ing.name;
        continue;
      }

      adjusted = true;
      for (const k of MACRO_KEYS) {
        const m = per.macros[k] ?? 0;
        contributedTotal[k] += plannedInPerUnit * m;
        actualForMeal[k] += actualInPerUnit * m;
      }
    }
  }

  if (unconvertible) {
    return {
      macros: plain,
      adjusted: false,
      problem:
        `“${unconvertible}” is measured in units its nutrition figure can't be ` +
        `converted to. Quote the per-unit macros in the same family the recipe ` +
        `uses (both weight, or both volume).`,
    };
  }

  if (!adjusted) return { macros: plain, adjusted: false };

  // The rest of the dish, at full recipe yield.
  const remainderTotal: Required<Macros> = { ...ZERO_MACROS };
  for (const k of MACRO_KEYS) {
    remainderTotal[k] = authoredTotal[k] - contributedTotal[k];
  }

  // A tolerance, because the authored figure is per-serving integers multiplied
  // back up — a recipe rounded to 40g/serving over 3 servings can legitimately
  // land a gram or two under the ingredient's true contribution. Only a real
  // contradiction should refuse to log.
  const offenders = MACRO_KEYS.filter((k) => remainderTotal[k] < -1);
  if (offenders.length > 0) {
    const worst = offenders.reduce((a, b) =>
      remainderTotal[a] < remainderTotal[b] ? a : b
    );
    return {
      macros: plain,
      adjusted: false,
      problem:
        `The recipe's macros don't square with its ingredients: after taking ` +
        `out the measured ones, the rest of the dish comes to ` +
        `${Math.round(remainderTotal[worst])} ${worst}. Fix the recipe's ` +
        `per-serving macros or the ingredient's per-unit figure.`,
    };
  }

  const macros: Required<Macros> = { ...ZERO_MACROS };
  for (const k of MACRO_KEYS) {
    // Remainder scales with portions the way it always has; the measured
    // ingredients are already this meal's amount and don't scale again.
    macros[k] = Math.max(0, remainderTotal[k]) / yield_ * portions + actualForMeal[k];
  }
  return { macros, adjusted: true };
}

// ── Batches ─────────────────────────────────────────────────────────
// Meat is bought per COOK, not per meal: one 2 lb pack becomes three dinners.
// So the amount worth typing is the pack, and the division is arithmetic the
// app should do rather than the cook.
//
// A batch is every meal of one recipe planned in the week being viewed. That
// needs no new stored concept — On Deck and the shopping list are already
// week-shaped, and a week is what one shop covers. Two weeks are two batches,
// which is right, because they're two shops.

/** What the recipe itself calls for across a whole batch, in the ingredient's
 *  own unit — the amount the cook is replacing when they type the pack size. */
export function batchPlannedAmount(
  meals: PlannedMeal[],
  recipe: Recipe | undefined,
  key: string
): number | null {
  if (!recipe) return null;
  for (const group of recipe.ingredientGroups ?? []) {
    for (const ing of group.ingredients ?? []) {
      if (normalizeName(ing.name ?? "") !== key) continue;
      const base = baseAmount(ing);
      if (base == null) return null;
      const servings = meals.reduce((n, m) => n + (m.servings || 1), 0);
      return (base * servings) / (recipe.servings || 1);
    }
  }
  return null;
}

/**
 * Divide a batch total across the meals cooked from it.
 *
 * In proportion to each meal's planned portions, not evenly — a 2-serving meal
 * takes twice what a 1-serving one does, which is how the recipe's own amount
 * already scales. Splitting evenly would quietly under-feed the bigger day.
 */
export function spreadAcrossBatch(total: number, meals: PlannedMeal[]): number[] {
  const servings = meals.reduce((n, m) => n + (m.servings || 1), 0);
  if (servings <= 0) return meals.map(() => 0);
  return meals.map((m) => (total * (m.servings || 1)) / servings);
}

/** What the cook actually bought for this batch, when every meal in it has
 *  been given an amount. Null if any meal is still on the recipe's figure —
 *  a partial batch has no honest total to show. */
export function batchActualAmount(
  meals: PlannedMeal[],
  key: string
): number | null {
  let sum = 0;
  for (const m of meals) {
    const v = m.ingredientAmounts?.[key];
    if (v == null) return null;
    sum += v;
  }
  return meals.length > 0 ? sum : null;
}

/** Sum a set of meals, resolving each against the cookbook. Meals that don't
 *  reconcile fall back to their authored figure (mealMacros does that), so a
 *  day total never silently loses a meal. */
export function sumMealMacros(
  meals: PlannedMeal[],
  byId: Map<string, Recipe>
): Required<Macros> {
  const t = { ...ZERO_MACROS };
  for (const m of meals) {
    const { macros } = mealMacros(m, byId.get(m.recipeId));
    for (const k of MACRO_KEYS) t[k] += macros[k];
  }
  return t;
}
